import React, {useEffect, useRef} from 'react';
import {canvas} from "leaflet/src/layer";

const EnergyChart = ({ name, Data, filters, Draw }) => {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const months = [
    "januari",
    "februari",
    "maart",
    "april",
    "mei",
    "juni",
    "juli",
    "augustus",
    "september",
    "oktober",
    "november",
    "december"
  ]

  const getDimensions = () => {
    if (!containerRef.current || !Data || Data.length === 0) return null;

    const container = containerRef.current;
    const padding = {
      leftPadding: 45,
      topPadding: 10,
      bottomPadding: 40,
      rightPadding: 10,
    };

    const width = Draw ? 1000 : container.clientWidth - padding.leftPadding - padding.rightPadding;
    const height = Draw ? 500 : container.clientHeight - padding.topPadding - padding.bottomPadding;

    if (width <= 0 || height <= 0 || Data.error) return null;

    const maxTotalWatt = Math.max(...Data.map(o => o?.totalWatt.value || 0));
    const maxOpladenWatt = Math.max(...Data.map(o => o?.opladenWatt.value || 0));
    const maxWatt = Math.max(maxOpladenWatt, maxTotalWatt);
    const maxHeight = Math.max(maxWatt / 1000, 1);

    return {
      padding,
      width,
      height,
      lineHeight: height / 10 || 0,
      lineWidth: width / Data.length || 0,
      maxHeight: maxHeight || 0,
      wattHeight: height / maxHeight || 0
    };
  }

  const drawLines = (context, dimensions) => {
    if (!context || !Data) return null;

    context.beginPath();
    context.strokeStyle = "#999";
    context.lineWidth = 1;

    for(let i = 0; i < 11; i++) {
      context.moveTo(dimensions.padding.leftPadding, dimensions.padding.topPadding + dimensions.lineHeight * i);
      context.lineTo(dimensions.padding.leftPadding + dimensions.width, dimensions.padding.topPadding + dimensions.lineHeight * i);
      context.fillText(((10 - i) * (dimensions.maxHeight / 10)).toFixed(0).toString() + " Kwh", 0, dimensions.lineHeight * i + dimensions.lineHeight / 4 + 5);
    }

    context.stroke()
  }

  const drawData = (context, dimensions) => {
    if (!context || !dimensions || !Data) return null;
    let legenda = {}

    context.font = "bold 12px Arial"

    Data.map((data, i) => {
      let indexHeight = {}

      Object.entries(data).filter((e) => filters[e[0]]?.value).map((element, index) => {
        if (!element[1].value) return
        element = element[1]
        context.beginPath();
        context.strokeStyle = element.color;
        context.lineWidth = element.lineWidth;

        if (!indexHeight[element.index]) indexHeight[element.index] = {}
        if (!indexHeight[element.index][element.zIndex]) indexHeight[element.index][element.zIndex] = 0

        context.moveTo(dimensions.padding.leftPadding + dimensions.lineWidth * (i+0.5) + 25 * element.index + element.lineOffset, dimensions.height + dimensions.padding.topPadding - indexHeight[element.index][element.zIndex]);
        context.lineTo(dimensions.padding.leftPadding + dimensions.lineWidth * (i+0.5) + 25 * element.index + element.lineOffset, dimensions.height + dimensions.padding.topPadding - dimensions.wattHeight * (element.value / 1000) - indexHeight[element.index][element.zIndex]);

        context.stroke()

        indexHeight[element.index][element.zIndex] += dimensions.wattHeight * (element.value / 1000);

        if (!legenda[element.name]) legenda[element.name] = {name: element.name, color: element.color};
      })

      context.fillText(data.date, dimensions.padding.leftPadding + dimensions.lineWidth * (i+0.5) - context.measureText(data.date).width / 2, dimensions.height + dimensions.padding.topPadding + dimensions.padding.bottomPadding / 3)
    })

    let legendaSize = 0

    Object.values(legenda).map((element, index) => {
      context.fillStyle = element.color;
      context.fillRect(10 + legendaSize, dimensions.height + dimensions.padding.topPadding + dimensions.padding.bottomPadding - 4 - 12, 12, 12)
      context.fillStyle = "#000000";
      context.fillText(element.name, 26 + legendaSize, dimensions.height + dimensions.padding.topPadding + dimensions.padding.bottomPadding - 5)

      legendaSize += context.measureText(element.name).width + 26;
    })
  }

  const draw = () => {
    if (Data?.error) {
      error()
      return
    }
    if (!canvasRef && canvasRef.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const dimensions = getDimensions();

    if (!dimensions) return;

    const container = containerRef.current;
    if (container) {
      canvas.width = dimensions.width + dimensions.padding.leftPadding + dimensions.padding.rightPadding;
      canvas.height = dimensions.height + dimensions.padding.topPadding + dimensions.padding.bottomPadding;
    }

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    drawLines(ctx, dimensions);

    drawData(ctx, dimensions);
  }

  const error = () => {
    if (!canvasRef && canvasRef.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const container = containerRef.current;
    if (container) {
      canvas.width = container.clientWidth;
      canvas.height = container.clientHeight;
    }

    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.fillStyle = "#000000";
    ctx.font = "80px Arial"
    const size = ctx.measureText(Data.error);
    ctx.fillText(Data.error, canvas.width / 2 - size.width / 2, canvas.height / 2)
  }

  const loading = () => {
    console.log("Loading...");

    if (!canvasRef && canvasRef.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const container = containerRef.current;
    if (container) {
      canvas.width = container.clientWidth;
      canvas.height = container.clientHeight;
    }

    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.fillStyle = "#000000";
    ctx.font = "120px Arial"
    const size = ctx.measureText("Laden...");
    ctx.fillText("Laden...", canvas.width / 2 - size.width / 2, canvas.height / 2)
  }

  const drawMouse = (e) => {
    if (!canvasRef && canvasRef.current || !Data) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const dimensions = getDimensions();

    if (!dimensions) return;

    const container = containerRef.current;
    if (container) {
      canvas.width = container.clientWidth;
      canvas.height = container.clientHeight;
    }

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    drawLines(ctx, dimensions);

    drawData(ctx, dimensions);

    const padding = 5
    const block = 10
    const gap = 13

    const rect = e.target.getBoundingClientRect();
    const x = e.clientX - rect.left + gap;
    const index = Math.ceil((e.clientX - rect.left - dimensions.padding.leftPadding) / dimensions.lineWidth);
    const y = e.clientY-rect.top

    if (index<1) return;

    if (y > dimensions.padding.topPadding && y < dimensions.height + dimensions.padding.topPadding && Data[index-1]) {
      ctx.beginPath();
      ctx.fillStyle = "#DDD";
      ctx.strokeStyle = "#000";
      ctx.globalAlpha = 0.85
      ctx.lineWidth = 1
      ctx.roundRect(x, y, 100, (Object.values(filters).filter((e) => e.value).length + 1) * (gap) + padding * 2, 3);
      ctx.fill()
      ctx.globalAlpha = 1
      ctx.stroke()

      ctx.fillStyle = "#000000";
      ctx.fillText(Data[index-1].date.split("-")[0] + " " + months[parseInt(Data[index-1].date.split("-")[1]) - 1], x + padding, y + gap + padding / 2);

      const InfoDataList = Object.values(Data[index-1]).filter((e) => Object.values(filters).find((event) => e.name === event.name)?.value)

      if (Data[index-1]) InfoDataList.map((element, i) => {
        if (!element.value) return

        ctx.fillStyle = "#000000";
        ctx.fillText((element.value / 1000).toFixed(1) + " kWh", x + padding + gap, y + gap * (i + 2) + padding);
        ctx.fillStyle = element.color;
        ctx.fillRect(x + padding, y + gap * (i + 2) - padding, block, block)
      })
    }

  }

  useEffect(() => {
    if(Data) draw()
    else loading()
  }, [Data, filters])

  window.addEventListener("resize", draw);

  return (
    <div onMouseLeave={draw} onMouseMove={(e) => {drawMouse(e)}} ref={containerRef} id={"container-" + name} className="w-full h-96">
      <canvas ref={canvasRef} id={"canvas-" + name}></canvas>
    </div>
  );
};

export default EnergyChart;
